import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAX_REASONS, reasonIdsFor, type CorridoReasonKind } from "@/lib/corrido-reasons";

// ReasonChips — "What's wrong? (optional)" as small one-tap toggles beside a
// redraw (board inspector, phone sheet), a new take or replaced scene (clip
// review), the fix pass and the owner's Taste page. Picking none is fine: the
// redraw goes out exactly as before. What is picked travels with the request
// as `reasons` and only teaches the tool (lib/corrido-reasons.ts).
//
// Kept small on purpose: on the board it sits in the inspector ABOVE Render,
// and nothing there may push the price question out of sight (1366x650).
// Inline styles, so it looks the same on the Tailwind timeline, the canvas
// cards and the phone sheet.

/** The chips a parent holds: cleared whenever `resetKey` changes (another tile
 *  or row selected). Call the returned `clear` after the request is sent. */
export function useReasonPicks(resetKey: unknown): [string[], (next: string[]) => void, () => void] {
  const [picks, setPicks] = useState<string[]>([]);
  useEffect(() => { setPicks([]); }, [resetKey]);
  return [picks, setPicks, () => setPicks([])];
}

export default function ReasonChips({
  kind, value, onChange, disabled = false, hint = true, testId = "reason-chips", heading,
}: {
  kind: CorridoReasonKind;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** The one-line "helps the tool learn" note after the chips. */
  hint?: boolean;
  testId?: string;
  /** Replaces the default "What's wrong? (optional)" lead; "" shows none. */
  heading?: string;
}) {
  const { t } = useTranslation();
  const ids = reasonIdsFor(kind);
  const picked = value.filter((id) => ids.includes(id));
  const full = picked.length >= MAX_REASONS;
  const lead = heading ?? t("corridoLearning.reasonsHeading");
  const label = lead || t("corridoLearning.reasonsHeading");
  const toggle = (id: string) => {
    if (picked.includes(id)) onChange(picked.filter((x) => x !== id));
    else if (!full) onChange([...picked, id]);
  };
  return (
    <div
      role="group" aria-label={label} data-testid={testId}
      // The rows and cards around these chips select or drag on a press.
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 4 }}
    >
      {lead && <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, marginRight: 2 }}>{lead}</span>}
      {ids.map((id) => {
        const on = picked.includes(id);
        const off = disabled || (!on && full);
        return (
          <button
            key={id} type="button" aria-pressed={on} disabled={off}
            data-testid={`${testId}-${id}`}
            onClick={() => toggle(id)}
            style={{
              background: on ? "rgba(248,113,113,.16)" : "transparent",
              border: `1px solid ${on ? "#f87171" : "#2a3648"}`,
              borderRadius: 999, color: on ? "#fecaca" : "#cbd5e1", cursor: off ? "default" : "pointer",
              fontSize: 11, fontWeight: on ? 700 : 500, lineHeight: "16px", opacity: off && !on ? 0.45 : 1,
              padding: "1px 8px", whiteSpace: "nowrap",
            }}
          >
            {t(`corridoReasons.${id}`)}
          </button>
        );
      })}
      {hint && <span style={{ color: "#7c8aa0", fontSize: 10.5, marginLeft: 2 }}>{t("corridoLearning.reasonsHint")}</span>}
    </div>
  );
}
